import React from 'react';
import { Link } from 'react-router-dom';
import { HeroSection } from '../components/HeroSection';
import { Calendar, ArrowRight, Search } from 'lucide-react';
import { motion } from 'framer-motion';
export function Blog() {
  const recentPosts = [{
    slug: 'architecture-of-exclusion',
    title: 'The Architecture of Exclusion: How Public Spaces Are Designed to Police Fat Bodies',
    excerpt: "From subway turnstiles to restaurant booths, the built environment tells fat people we don't belong.",
    category: 'Essay',
    date: '2024-01-15',
    color: 'border-brand-magenta'
  }, {
    slug: 'hunger-as-resistance',
    title: 'Hunger as Resistance',
    excerpt: 'A collection of poems reclaiming appetite in a world that demands our starvation.',
    category: 'Poetry',
    date: '2024-01-10',
    color: 'border-brand-lime'
  }, {
    slug: 'beyond-representation',
    title: "Beyond Representation: Why Seeing Fat People on TV Isn't Enough",
    excerpt: 'Visibility without structural change is just better PR for the same oppressive systems.',
    category: 'Critique',
    date: '2024-01-05',
    color: 'border-brand-cyan'
  }];
  return <div className="min-h-screen bg-brand-cream" data-id="element-335">
      <HeroSection title="The Blog" subtitle="Thoughts, updates, and reflections from the ReGrowth team and community." tag="Journal" bgColor="orange" data-id="element-336" />

      {/* Featured Post */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto -mt-12 relative z-10" data-id="element-337">
        <Link to="/blog/why-we-built-regrowth" className="block group" data-id="element-338">
          <motion.article initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="bg-white border-2 border-brand-black shadow-xl hover:shadow-2xl transition-all overflow-hidden flex flex-col md:flex-row" data-id="element-339">
            <div className="md:w-1/2 bg-[#1C3A2E] p-12 flex flex-col justify-center relative overflow-hidden" data-id="element-340">
              {/* Decorative SVG Background */}
              <div className="absolute inset-0 opacity-20 pointer-events-none flex items-end justify-center" data-id="element-341">
                <svg viewBox="0 0 400 400" className="w-full h-auto" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-342">
                  <circle cx="200" cy="400" r="150" stroke="#FFFBF5" strokeWidth="2" data-id="element-343" />
                  <circle cx="200" cy="400" r="250" stroke="#FFFBF5" strokeWidth="2" data-id="element-344" />
                </svg>
              </div>
              <div className="relative z-10" data-id="element-345">
                <span className="inline-block px-3 py-1 mb-4 text-xs font-bold uppercase tracking-widest bg-brand-orange text-white" data-id="element-346">
                  Featured
                </span>
                <h2 className="text-3xl md:text-5xl font-black text-brand-cream mb-4 leading-tight group-hover:text-brand-orange transition-colors" data-id="element-347">
                  Why We Built ReGrowth
                </h2>
                <p className="text-brand-cream/80 text-lg" data-id="element-348">
                  A conversation with the founders about fat liberation,
                  learning, and building something that matters.
                </p>
              </div>
            </div>
            <div className="md:w-1/2 p-8 md:p-12 flex flex-col justify-center bg-white" data-id="element-349">
              <p className="text-gray-700 text-lg leading-relaxed mb-8" data-id="element-350">
                "There's a massive gap between academic fat studies and
                accessible, everyday education. You either have dense, paywalled
                academic papers, or you have watered-down 'body positivity' that
                ignores systemic oppression. We wanted to build a bridge."
              </p>
              <div className="flex items-center justify-between mt-auto pt-6 border-t border-gray-100" data-id="element-351">
                <div className="flex items-center gap-2 text-sm text-gray-500 font-medium" data-id="element-352">
                  <Calendar className="w-4 h-4" data-id="element-353" />
                  January 2025
                </div>
                <span className="inline-flex items-center font-bold text-brand-orange group-hover:translate-x-2 transition-transform" data-id="element-354">
                  Read Q&A <ArrowRight className="w-4 h-4 ml-2" data-id="element-355" />
                </span>
              </div>
            </div>
          </motion.article>
        </Link>
      </section>

      {/* Recent Posts Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-356">
        <h2 className="text-2xl font-black uppercase mb-8 text-brand-black" data-id="element-357">
          Recent Posts
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-358">
          {recentPosts.map((post, index) => <Link key={post.slug} to={`/blog/${post.slug}`} className="group" data-id="element-359">
              <motion.article initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className={`bg-white border-l-4 ${post.color} p-6 border-y-2 border-r-2 border-brand-black hover:shadow-lg transition-shadow h-full flex flex-col`} data-id="element-360">
                <span className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3 block" data-id="element-361">
                  {post.category}
                </span>
                <h3 className="text-xl font-black mb-3 text-brand-black group-hover:text-brand-orange transition-colors leading-tight" data-id="element-362">
                  {post.title}
                </h3>
                <p className="text-gray-700 text-sm mb-6 leading-relaxed flex-grow" data-id="element-363">
                  {post.excerpt}
                </p>
                <div className="flex items-center gap-2 text-xs text-gray-500 pt-4 border-t border-gray-100 mt-auto" data-id="element-364">
                  <Calendar className="h-3 w-3" data-id="element-365" />
                  <span data-id="element-366">
                    {new Date(post.date).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric'
                })}
                  </span>
                </div>
              </motion.article>
            </Link>)}
        </div>
      </section>

      {/* Browse Catalogue CTA */}
      <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pb-16" data-id="element-367">
        <Link to="/blog/catalogue" className="block group" data-id="element-368">
          <div className="border-2 border-brand-black bg-white p-8 flex flex-col sm:flex-row items-center justify-between gap-6 hover:shadow-lg transition-shadow" data-id="element-369">
            <div className="flex items-center gap-5" data-id="element-370">
              <div className="w-14 h-14 rounded-full bg-brand-navy flex items-center justify-center flex-shrink-0" data-id="element-371">
                <Search className="w-6 h-6 text-white" data-id="element-372" />
              </div>
              <div data-id="element-373">
                <h3 className="text-xl font-black uppercase text-brand-black group-hover:text-brand-orange transition-colors" data-id="element-374">
                  Search Our Blog Catalogue
                </h3>
                <p className="text-gray-600 text-sm mt-1" data-id="element-375">
                  Browse all posts by category, author, or keyword.
                </p>
              </div>
            </div>
            <ArrowRight className="w-6 h-6 text-brand-orange group-hover:translate-x-2 transition-transform flex-shrink-0" data-id="element-376" />
          </div>
        </Link>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 bg-brand-orange text-white" data-id="element-377">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-378">
          <h2 className="text-3xl font-black uppercase mb-6" data-id="element-379">
            Never Miss a Post
          </h2>
          <p className="text-xl mb-8 opacity-90" data-id="element-380">
            Get new essays, reporting, and updates delivered to your inbox
            monthly.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto" data-id="element-381">
            <input type="email" placeholder="Your email" className="flex-1 px-4 py-3 text-brand-black border-2 border-white focus:outline-none" data-id="element-382" />
            <button className="px-8 py-3 bg-white text-brand-orange font-bold uppercase tracking-wide hover:bg-gray-100 transition-colors" data-id="element-383">
              Subscribe
            </button>
          </div>
        </div>
      </section>
    </div>;
}